"use client";

import React, { useEffect, useRef } from "react";
import { GRID_SECTION_OFFSET_PROPERTY } from "./grid-section.shared";
import { STICKY_HEADER_ATTRIBUTE } from "./sticky-header.shared";
import styles from "./sticky-offset.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * GS8's offset — "section headers stay pinned under the package header while scrolling". The pin
 * itself is already free: `grid-section.module.css` sets `inset-block-start:
 * var(--cl-section-offset, 0)` on the summary.
 */
export interface StickyOffsetScopeProps {
  /** The view this offset governs: the pinned grid header and the sections
   *  under it, already built by the caller. */
  children: React.ReactNode;
}

function StickyOffsetScopeImpl({ children }: StickyOffsetScopeProps) {
  const scopeRef = useRef<HTMLDivElement>(null);
  const observedRef = useRef<{ header: HTMLElement; observer: ResizeObserver } | null>(null);
  /** The px the scope resolves now; seeded from the stylesheet's floor. */
  const publishedRef = useRef<number | null>(null);

  useEffect(() => () => observedRef.current?.observer.disconnect(), []);

  /* Re-run per `children` only to FIND a header that mounted later (a page
     that opened on Genre Packs); a header already observed returns at once, so
     a view switch costs one query and no measurement. */
  useEffect(() => {
    const scope = scopeRef.current;
    /* Guarded because jsdom ships no `ResizeObserver`, so a consumer's test would throw on mount.
       Not a browser-support guard. */
    if (!scope || typeof ResizeObserver !== "function") {
      return;
    }

    /* Found in the DOM rather than taken as a ref prop: the header crosses this boundary through
       `children` already rendered, and a ref prop on it would be a new client-facing API on a slot
       the composer builds on the server. */
    const header = scope.querySelector<HTMLElement>(`[${STICKY_HEADER_ATTRIBUTE}]`);
    if (!header || observedRef.current?.header === header) {
      return;
    }
    observedRef.current?.observer.disconnect();

    /* The border box, never the entry's `contentRect`: the band the pinned header covers is its
       border box — GH6 draws a hairline under it in forced colours — and a content-box measurement
       would leave every section header overlapping the grid header by that much. */
    const measure = () => {
      const height = Math.floor(header.getBoundingClientRect().height);
      /* 0 is a HIDDEN view (`hidden` on the kept-alive Signature view), not a
         header with no height: keep the last value for when it comes back. */
      if (height === 0) {
        return;
      }
      publishedRef.current ??= readOffset(scope);
      if (height !== publishedRef.current) {
        publishedRef.current = height;
        scope.style.setProperty(GRID_SECTION_OFFSET_PROPERTY, `${height}px`);
      }
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(header);
    observedRef.current = {
      header,
      observer
    };
  }, [children]);

  return (
    /* One declaration, and it is the property — no display, no position, no z-index. */
    <div className={styles.scope} ref={scopeRef}>
      {children}
    </div>
  );
}
export const StickyOffsetScope = withSafeRender(StickyOffsetScopeImpl, "StickyOffsetScope");

/** The stylesheet's floor as the scope resolves it before any write; px, the
 *  unit both writers use. NaN where no sheet applies, so the first write lands. */
function readOffset(scope: HTMLElement): number {
  return Number.parseFloat(getComputedStyle(scope).getPropertyValue(GRID_SECTION_OFFSET_PROPERTY));
}

export default StickyOffsetScope;
